import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Image as ImageIcon, RefreshCw, Sparkles, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import { api_get } from "../../lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminToken = () => localStorage.getItem("southtrip:adminToken");

const SLOTS = [
    { slot: "munnar", label: "Munnar — Tea hills" },
    { slot: "alleppey", label: "Alleppey — Backwaters" },
    { slot: "fort-kochi", label: "Fort Kochi — Chinese fishing nets" },
    { slot: "varkala", label: "Varkala — Cliff beach" },
    { slot: "thekkady", label: "Thekkady — Periyar elephants" },
];

const HeroImages = () => {
    const [current, setCurrent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const data = await api_get("/hero-images");
            setCurrent(Array.isArray(data) ? data : []);
        } catch {
            toast.error("Failed to load current hero images");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const regenerate = async () => {
        if (!window.confirm("Generate 5 new Kerala hero images via Gemini? This takes ~30–90 seconds and replaces the current ones.")) return;
        setGenerating(true);
        setLastResult(null);
        try {
            const res = await axios.post(
                `${API_BASE}/admin/hero-images/regenerate`,
                {},
                { headers: { Authorization: `Bearer ${adminToken()}` }, timeout: 240000 }
            );
            setLastResult(res.data);
            if (res.data.status === "ok") {
                toast.success(`Generated ${res.data.generated.length} hero images`);
            } else if (res.data.status === "partial") {
                toast.warning(`Generated ${res.data.generated.length}/5 — ${res.data.errors.length} failed`);
            } else {
                toast.error("Failed to generate hero images");
            }
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Generation failed");
        } finally {
            setGenerating(false);
        }
    };

    const byCurrentSlot = Object.fromEntries(current.map((c) => [c.slot, c]));

    return (
        <div className="space-y-6" data-testid="admin-hero-images-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-widest text-ink-light flex items-center gap-1.5">
                        <ImageIcon className="w-3.5 h-3.5" /> Hero carousel
                    </div>
                    <h1 className="font-display text-2xl text-ink font-bold mt-1">Home hero images</h1>
                    <p className="text-sm text-ink-light mt-1 max-w-2xl">
                        These 5 Kerala photos rotate in the home page hero. They are AI-generated via Gemini Nano Banana and
                        stored in your GridFS — no external dependency, no risk of broken links.
                    </p>
                </div>
                <button
                    onClick={regenerate}
                    disabled={generating}
                    className="px-4 py-2 rounded-lg bg-ink text-white font-semibold text-sm hover:bg-brand transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="hero-regenerate-btn"
                >
                    {generating ? (
                        <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Generating… (30–90 s)
                        </>
                    ) : (
                        <>
                            <Sparkles className="w-4 h-4" />
                            Regenerate all
                        </>
                    )}
                </button>
            </div>

            {/* Status banner */}
            {current.length === 0 && !loading && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="hero-empty-warning">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <strong>No hero images yet.</strong> Click <em>Regenerate all</em> above to populate the home carousel.
                        Without this, the home page shows the default SouthTrip placeholder.
                    </div>
                </div>
            )}
            {current.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="hero-active-banner">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-900">
                        <strong>{current.length} hero image{current.length > 1 ? "s" : ""} active.</strong> Carousel rotates every 6 seconds.
                    </div>
                </div>
            )}

            {/* Grid of 5 slots */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hero-slots-grid">
                {SLOTS.map((spec) => {
                    const cur = byCurrentSlot[spec.slot];
                    return (
                        <div key={spec.slot} className="bg-white border border-cream-200 rounded-xl overflow-hidden">
                            <div className="aspect-video bg-cream-100 relative">
                                {cur ? (
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${cur.url}`}
                                        alt={cur.alt}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-ink-light text-xs">
                                        Empty slot
                                    </div>
                                )}
                            </div>
                            <div className="p-3">
                                <div className="text-sm font-semibold text-ink">{spec.label}</div>
                                <div className="text-[11px] text-ink-light truncate mt-0.5">
                                    {cur ? cur.url : "Awaiting generation"}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Last result */}
            {lastResult && (
                <div className="bg-white border border-cream-200 rounded-xl p-4 text-sm" data-testid="hero-last-result">
                    <div className="font-semibold text-ink mb-2">Last generation result</div>
                    {lastResult.generated && lastResult.generated.length > 0 && (
                        <ul className="space-y-1">
                            {lastResult.generated.map((g) => (
                                <li key={g.slot} className="flex items-center gap-2 text-ink-light">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    <span className="font-mono text-xs">{g.slot}</span>
                                    <span className="text-xs">→ {(g.bytes / 1024).toFixed(0)} KB</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {lastResult.errors && lastResult.errors.length > 0 && (
                        <ul className="space-y-1 mt-2">
                            {lastResult.errors.map((e, i) => (
                                <li key={i} className="flex items-start gap-2 text-rose-700">
                                    <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                                    <span className="font-mono text-xs">{e.slot}</span>
                                    <span className="text-xs">— {e.error}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <p className="text-xs text-ink-light">
                Tip: open the <a href="/" target="_blank" rel="noreferrer" className="text-brand underline inline-flex items-center gap-1">home page <ExternalLink className="w-3 h-3" /></a> in a new tab after regenerating to see the new carousel.
            </p>
        </div>
    );
};

export default HeroImages;
